import { useEffect, useRef, forwardRef, useImperativeHandle, useState } from 'react';
import { Alert, Box, Button } from '@mui/material';

/**
 * Cloudflare Turnstile widget wrapper.
 *
 * Resilience: Cloudflare's challenge-platform endpoint can return errors that the bare
 * `render()` call swallows silently — the user sees an empty widget area and login is
 * blocked because no token ever arrives. We register the full callback set
 * (error / expired / timeout) and surface a friendly retry UI so the user is never stuck.
 *
 * Common error codes:
 *   110200 — Domain not allowed. Add the domain to the Turnstile widget allowlist
 *            (Cloudflare dashboard → Turnstile → widget → Domains).
 *   300xxx — Network / browser issues (often transient; retry usually works).
 *   600xxx — Challenge failed (browser flagged as bot-like).
 */

/*
 * Cloudflare's normal widget is a fixed 300px. A phone's form column is narrower
 * (270px on a 360 screen), so the check hung out past the fields, and on a 320
 * screen the card clipped its checkbox. Compact (150px) wherever 300 won't fit;
 * a wider column keeps the normal widget, so the desktop check is unchanged.
 * 0 means not laid out — keep normal rather than guess.
 */
const NORMAL_WIDGET_WIDTH = 300;
const widgetSize = (box) => {
  const room = box?.clientWidth || 0;
  return room > 0 && room < NORMAL_WIDGET_WIDTH ? 'compact' : 'normal';
};

const TurnStile = forwardRef(({ setToken: setTokenProp, onError: onErrorProp }, ref) => {
  const widgetRef = useRef(null);
  const widgetIdRef = useRef(null);
  // The callbacks live in refs so the widget effect never depends on them. It
  // re-renders the widget (remove + render) whenever it re-runs, and a caller
  // passing inline functions re-ran it on every render of the page: a keystroke
  // or a snackbar reset the challenge and wiped a ticked checkbox.
  const setTokenRef = useRef(setTokenProp);
  const onErrorRef = useRef(onErrorProp);
  setTokenRef.current = setTokenProp;
  onErrorRef.current = onErrorProp;
  const setToken = (token) => setTokenRef.current && setTokenRef.current(token);
  const onError = (err) => onErrorRef.current && onErrorRef.current(err);
  const [error, setError] = useState(null); // { code, message } or null
  const [scriptMissing, setScriptMissing] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    // The api.js script is loaded async from index.html. Poll briefly for it so that on
    // a slow connection (or in incognito with no cached script) we don't permanently
    // skip render after a single check.
    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 50; // 50 × 100ms = 5s
    const poll = () => {
      if (cancelled) return;
      if (window.turnstile) {
        renderWidget();
        return;
      }
      if (++attempts >= MAX_ATTEMPTS) {
        setScriptMissing(true);
        return;
      }
      setTimeout(poll, 100);
    };

    const renderWidget = () => {
      if (!widgetRef.current || widgetRef.current.hasChildNodes()) return;
      try {
        widgetIdRef.current = window.turnstile.render(widgetRef.current, {
          sitekey: process.env.REACT_APP_TURNSTILE_SITE_KEY,
          appearance: 'interaction-only',
          // Measured on the column, not the widget's own div: that is an empty
          // centred flex item, 0px wide until Cloudflare fills it.
          size: widgetSize(widgetRef.current.parentElement),
          retry: 'auto',
          'retry-interval': 8000,
          callback: (token) => {
            setError(null);
            setToken(token);
          },
          'error-callback': (code) => {
            const message = describeTurnstileError(code);
            setError({ code, message });
            setToken(null);
            onError({ code, message });
            // 110200 (domain not allowed) is permanent for this site — no point auto-retrying.
            return code !== '110200';
          },
          'expired-callback': () => {
            setToken(null);
            if (window.turnstile && widgetIdRef.current !== null) {
              window.turnstile.reset(widgetIdRef.current);
            }
          },
          'timeout-callback': () => {
            setToken(null);
            if (window.turnstile && widgetIdRef.current !== null) {
              window.turnstile.reset(widgetIdRef.current);
            }
          },
        });
      } catch (err) {
        const message = err?.message || 'Failed to load verification widget.';
        setError({ code: 'render-failed', message });
        onError({ code: 'render-failed', message });
      }
    };

    poll();

    return () => {
      cancelled = true;
      if (window.turnstile && widgetIdRef.current !== null) {
        try { window.turnstile.remove(widgetIdRef.current); } catch (_) { /* ignore */ }
        widgetIdRef.current = null;
      }
    };
    // retryNonce intentionally re-runs the effect when the user clicks Retry.
  }, [retryNonce]); // eslint-disable-line

  useImperativeHandle(ref, () => ({
    resetToken: () => {
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.reset(widgetIdRef.current);
      }
    },
  }));

  const handleRetry = () => {
    setError(null);
    setScriptMissing(false);
    if (widgetRef.current) widgetRef.current.innerHTML = '';
    widgetIdRef.current = null;
    setRetryNonce((n) => n + 1);
  };

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
      <div ref={widgetRef}></div>
      {scriptMissing && (
        <Alert severity="warning" sx={{ width: '100%', maxWidth: 360, fontSize: '0.82rem' }}
          action={<Button size="small" onClick={handleRetry}>Retry</Button>}>
          Verification could not load. Check your connection or disable any ad/script blockers and retry.
        </Alert>
      )}
      {error && !scriptMissing && (
        <Alert severity="error" sx={{ width: '100%', maxWidth: 360, fontSize: '0.82rem' }}
          action={error.code !== '110200' ? <Button size="small" onClick={handleRetry}>Retry</Button> : null}>
          {error.message} {error.code && <span style={{ opacity: 0.7 }}>({error.code})</span>}
        </Alert>
      )}
    </Box>
  );
});

// Map Cloudflare Turnstile error codes to actionable user-facing messages.
function describeTurnstileError(code) {
  const c = String(code || '');
  if (c === '110200') return 'This site is not configured for verification on this domain. Please contact support.';
  if (c.startsWith('1102')) return 'Verification was blocked by browser settings. Disable strict tracking protection or ad-blockers and retry.';
  if (c.startsWith('300') || c.startsWith('600')) return 'Verification failed. Please retry.';
  return 'Verification failed. Please retry.';
}

export default TurnStile;
